<script lang="ts">
import TowerCard from '@/views/tower_visualizer/TowerCard.vue';
import { ProgramTower } from '@/types/tower/v2/Tower';
import DeleteTowerModal from '@/views/tower_dashboard/DeleteTowerModal.vue';
import { TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { NEW_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { BOUND_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { RENEWAL_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { reactive } from 'vue';
import { Plus } from 'lucide-vue-next';
import CreateTowerModal from '@/views/tower_dashboard/CreateTowerModal.vue';
import { IsUserAClient } from '@/utilities/AuthUtils';

export default {
  name: 'AccountTowerDashboard',
  components: {
    TowerCard,
  },
};
</script>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { GetTowerCardsParams, GetTowersParams, towerService } from '@/services/v2/TowerService';
import { TowerCardData, TowerTypeData } from '@/views/tower_dashboard/types/tower-dashboard';
import { useRouter } from 'vue-router';
import { AppState, Commit } from '@/store';
import EditTowerModal from '@/views/tower_dashboard/EditTowerModal.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useStore } from 'vuex';
import { escapeRegex } from '@/utilities/tower/regex';

// ------
// props
// ------
const props = defineProps<{
  accountId: string;
  programId: string;
}>();

const emit = defineEmits<{
  viewTowers: [serializedTower: string];
}>();

const store = useStore<AppState>();

// ---------------
// reactive data
// ---------------
const router = useRouter();
const towerCards = ref<TowerCardData[]>([]);
const selectedCardIds = ref<string[]>([]);
const loading = ref(true);
const showRenameTowerModal = ref(false);
const deleteTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerCard?: TowerCardData;
}>({ show: false, highlightTowerId: undefined, towerCard: undefined });
const searchFilter = ref('');
const searchTimeout = ref<NodeJS.Timeout>();
const towerTypes = ref<TowerTypeData[]>();
const editTowerId = ref<string>();
const disableTowers = ref<false>();
const newTowerModalData = reactive({
  show: false,
  type: TowerType.BoundCoverage,
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  loading.value = true;
  await fetchTowerCards();
  towerTypes.value = await towerService.getTowerTypes();
  loading.value = false;
});

// ---------
// watchers
// ---------
watch(searchFilter, () => {
  if (loading.value) {
    return;
  }
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  searchTimeout.value = setTimeout(() => {
    fetchTowerCards();
  }, 400);
});

// ---------
// computed
// ---------
const isFiltered = computed(() => {
  return !!searchFilter.value;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// --------
// methods
// --------
async function fetchTowerCards() {
  const params: GetTowerCardsParams = {
    searchQuery: searchFilter.value,
    accountId: Number(props.accountId),
    programId: Number(props.programId),
  };

  towerCards.value = await towerService.getTowerCards(params);

  if (isUserAClient.value) {
    towerCards.value = towerCards.value
      .filter(towerCard => towerCard.published)
      .map(towerCard => ({ ...towerCard, contributors: '' }));
  }

  for (const towerCard of towerCards.value) {
    if (towerCard.towerTypeId === TowerType.MultiTower) {
      towerCard.towerCards = [];
      const tower = (await towerService.getTowers({ towerIds: [towerCard.towerId] }))[0];
      if (tower) {
        const towerObject = JSON.parse(tower.towerObject);
        for (const object of towerObject.objects) {
          if (object['towerCard']) {
            towerCard.towerCards.push(object['towerCard']);
          }
        }
      }
    }
  }

  loading.value = false;
}

function handleTowerSelect(towerId: string, selected: boolean) {
  if (selected) {
    selectedCardIds.value.push(towerId);
  } else {
    selectedCardIds.value = selectedCardIds.value.filter((id) => id !== towerId);
  }
}

function handleEditTower(towerId: string) {
  const towerCard = towerCards.value.find((t) => t.towerId === towerId);
  if (towerCard?.towerTypeId === 5) {
    router.push('towers/' + towerId);
  } else {
    router.push('/v2/tower/' + towerId);
  }
}

function openRenameTowerModal(towerId: string) {
  const tower = towerCards.value?.filter((t) => t.towerId === towerId)[0];
  if (tower) {
    editTowerId.value = tower.towerId;
    showRenameTowerModal.value = true;
  }
}

function handleRenameTower(success: boolean) {
  if (success) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Tower updated successfully' });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Could not update tower', color: 'error' });
  }

  showRenameTowerModal.value = false;
  fetchTowerCards();
}

async function handleDuplicateTower(towerId: string) {
  // find count of matching tower versions for this tower (by programId and tower type)
  // add 1 to that count to get the new version number
  const tower: ProgramTower = (await towerService.getTowers({ towerIds: [towerId] }))[0];

  if (!tower) {
    return;
  }

  const params = {
    programId: tower.programId ? Number(tower.programId) : 0,
    towerTypeId: tower.towerTypeId,
    towerName: tower.towerName,
  } as GetTowersParams;

  const towers = await towerService.getTowers(params);

  if (!towers || towers.length === 0) {
    return;
  }

  const baseVersionLabel = tower.versionLabel?.replace(/(?: \(\d+\))?$/, '') ?? '';
  const duplicateLabelRegex = new RegExp(`^${escapeRegex(baseVersionLabel)}(?: \\(\\d+\\))?$`);

  const versionLabelCount = towers.reduce((acc, t) => {
    if (t.versionLabel && t.versionLabel.match(duplicateLabelRegex)) {
      acc++;
    }
    return acc;
  }, 0);

  if (versionLabelCount === 0) {
    return;
  } else {
    tower.versionLabel = `${baseVersionLabel} (${versionLabelCount})`;
  }

  await towerService.createTower(tower);
  await fetchTowerCards();
}

function handleConfirmDeleteTower(towerCard: TowerCardData) {
  deleteTowerState.value = {
    show: true,
    highlightTowerId: towerCard.towerId,
    towerCard,
  };
}

async function handleDeleteTower(towerId?: string) {
  if (!towerId) {
    return;
  }

  await towerService.deleteTower(towerId);
  deleteTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerCard: undefined,
  };
  await fetchTowerCards();
}

function resetFilters() {
  searchFilter.value = '';
}

async function handleCreateTowerAction() {
  emit('viewTowers', await createSerializedTower());
}

async function createSerializedTower(): Promise<string> {
  try {
    const tempCanvas = new fabric.StaticCanvas(null, {});
    const canvas = new fabric.StaticCanvas(null, {});

    const towers = await towerService.getTowers({ towerIds: selectedCardIds.value });

    for (const tower of towers) {
      const towerObject = JSON.parse(tower.towerObject);
      tempCanvas.loadFromJSON(towerObject, () => {
        tempCanvas.renderAll();

        const group = new fabric.GroupBase(tempCanvas.getObjects(), {} as fabric.GroupBaseOptions);
        group.set({
          id: tower.towerId,
          towerCard: towerCards.value.find((t) => t.towerId === tower.towerId),
          scaleX: 0.7,
          scaleY: 0.7,
        } as fabric.GroupBaseOptions);
        canvas.add(group);
        group.centerH();
        group.centerV();
        canvas.renderAll();
        tempCanvas.clear();
      });
    }

    return JSON.stringify(canvas.toJSON());
  } catch (e) {
    console.error(e);
    return '';
  }
}

function openNewTowerModal(type: TowerType) {
  newTowerModalData.type = type;
  newTowerModalData.show = true;
}

function handleCreateTower(towerId: string) {
  router.push('/v2/tower/' + towerId);
} 
</script>

<template>
  <div class="tw:flex tw:flex-col">
    <div class="tw:mt-6 tw:flex tw:h-full tw:flex-col">
      <!-- Overlay to disable editing while card options are open -->
      <div
        v-if="disableTowers"
        @click="$event.stopPropagation()"
        class="tw:pointer-events-all tw:absolute tw:z-10 tw:h-full tw:w-full tw:cursor-auto"
      ></div>

      <!-- options -->
      <div class="tw:z-100 tw:flex tw:w-10/12 tw:flex-row tw:items-center tw:justify-center tw:gap-6 tw:self-center">
        <!-- Search bar -->
        <input
          type="text"
          placeholder="Search"
          v-model="searchFilter"
          class="tw:input tw:w-64 tw:border-solid tw:shadow-xs"
        />

        <button
          v-if="isFiltered"
          @click="resetFilters"
          :disabled="!isFiltered"
          class="tw:h-fit tw:p-0 tw:text-xs tw:font-bold tw:uppercase tw:transition-all tw:duration-100"
          :class="isFiltered ? 'tw:bg-white tw:text-red-500 tw:hover:scale-110' : 'tw:bg-red-50 tw:text-gray-400'"
        >
          Reset
        </button>
        <div class="tw:join">
          <button
            class="tw:btn tw:btn-primary tw:join-item tw:ml-8"
            :class="{ 'tw:btn-disabled': selectedCardIds.length === 0 }"
            @click="handleCreateTowerAction"
          >
            View Towers
          </button>
        </div>
      </div>

      <!--  card container -->
      <div v-if="loading" class="tw:mt-10 tw:flex tw:items-center tw:justify-center">
        <span class="tw:loading tw:loading-dots tw:loading-lg tw:text-gray-600"></span>
      </div>
      <div
        v-else
        class="tw:mt-8 tw:flex tw:w-10/12 tw:flex-row tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-8 tw:gap-y-6 tw:self-center tw:sm:gap-x-4 tw:sm:gap-y-6 tw:lg:gap-x-4 tw:lg:gap-y-6 tw:xl:gap-x-10 tw:xl:gap-y-8 tw:2xl:gap-x-14 tw:2xl:gap-y-10"
      >
        <!-- Tower creation cards -->
        <div
          class="tw:flex tw:h-56 tw:w-80 tw:flex-row tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-6 tw:self-center"
          v-if="!isUserAClient"
        >
          <!-- bound coverage card -->
          <div
            @click="openNewTowerModal(TowerType.BoundCoverage)"
            class="tw:group tw:tooltip tw:tooltip-bottom tw:pointer-events-auto tw:z-30 tw:flex tw:cursor-pointer tw:justify-items-center"
            :data-tip="BOUND_COVERAGE_TOOLTIP"
          >
            <div
              class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-blue-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
            >
              <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
                <Plus class="tw:text-gray-400" />
                <h2 class="tw:text-center tw:text-sm tw:font-medium">Bound Coverage</h2>
              </div>
            </div>
          </div>

          <!-- new coverage card -->
          <div
            @click="openNewTowerModal(TowerType.NewCoverage)"
            class="tw:group tw:tooltip tw:tooltip-bottom tw:pointer-events-auto tw:z-30 tw:flex tw:cursor-pointer tw:justify-items-center"
            :data-tip="NEW_COVERAGE_TOOLTIP"
          >
            <div
              class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-orange-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
            >
              <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
                <Plus class="tw:text-gray-400" />
                <h2 class="tw:text-center tw:text-sm tw:font-medium">New Coverage</h2>
              </div>
            </div>
          </div>

          <!-- new renewal card -->
          <div
            @click="openNewTowerModal(TowerType.Renewal)"
            class="tw:group tw:tooltip tw:tooltip-bottom tw:pointer-events-auto tw:z-20 tw:flex tw:cursor-pointer tw:justify-items-center"
            :data-tip="RENEWAL_TOOLTIP"
          >
            <div
              class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-red-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
            >
              <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
                <Plus class="tw:text-gray-400" />
                <span class="tw:text-center tw:text-sm tw:font-medium">Renewal</span>
              </div>
            </div>
          </div>
        </div>

        <TowerCard
          v-for="(card, index) in towerCards"
          :key="index"
          :card-data="card"
          @click="handleTowerSelect"
          @rename="openRenameTowerModal"
          @edit="handleEditTower"
          @duplicate="handleDuplicateTower"
          @delete="handleConfirmDeleteTower"
          :highlightTowerId="deleteTowerState.highlightTowerId"
          v-model:options-open="disableTowers"
        />
      </div>

      <EditTowerModal
        v-if="showRenameTowerModal"
        :tower-id="editTowerId"
        @close="showRenameTowerModal = false"
        @update="handleRenameTower"
      />
      <DeleteTowerModal
        :show="deleteTowerState.show"
        :tower-card="deleteTowerState.towerCard"
        :handle-cancel="() => (deleteTowerState = { show: false, towerCard: undefined })"
        :handle-action="(towerId: string | undefined) => handleDeleteTower(towerId)"
      />

      <CreateTowerModal
        :show="newTowerModalData.show"
        :tower-type="newTowerModalData.type"
        :account-id="accountId"
        :program-id="programId"
        @close="newTowerModalData.show = false"
        @create="handleCreateTower"
      />
    </div>
  </div>
</template>
